import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_SLIDES = [
  { src: "/app-preview.png", url: "/app/dashboard", alt: "Leadmato dashboard - scans run, leads found, saved leads and scans left, plus signal growth trend, leads saved, source breakdown and top prospects" },
  { src: "/app-preview-2.png", url: "/app/find-customers", alt: "Leadmato Find Customers - real buyer posts from Reddit with intent, score, matched keyword and one-click save lead" },
  { src: "/app-preview-3.png", url: "/app/brand", alt: "Leadmato Brand Foundation - brand fit score, buyer keywords ticked ready for scans, category and market set for AI VidSummary" },
];
import { toast } from "sonner";
import { joinWaitlist, EMAIL_RE } from "@/lib/waitlist";
import JunkFilteredChip from "@/components/JunkFilteredChip";

// Similar-length phrases (~18-21 chars) that all fit the hero rhythm and match
// Leadmato's brand voice (Indian SMB, benefit-first, no jargon).
const HEADLINE_PHRASES = [
  "Get real customers.",
  "Find warm leads now.",
  "Close more this week.",
];

function useTypewriter(phrases, {
  typingSpeed = 55,
  deletingSpeed = 30,
  holdBeforeDelete = 1600,
  holdBeforeNext = 400,
} = {}) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | holding | deleting | switching

  useEffect(() => {
    const current = phrases[idx];
    let t;
    if (phase === "typing") {
      if (text.length < current.length) {
        t = setTimeout(() => setText(current.slice(0, text.length + 1)), typingSpeed);
      } else {
        t = setTimeout(() => setPhase("deleting"), holdBeforeDelete);
      }
    } else if (phase === "deleting") {
      if (text.length > 0) {
        t = setTimeout(() => setText(current.slice(0, text.length - 1)), deletingSpeed);
      } else {
        t = setTimeout(() => {
          setIdx((i) => (i + 1) % phrases.length);
          setPhase("typing");
        }, holdBeforeNext);
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, phrases, typingSpeed, deletingSpeed, holdBeforeDelete, holdBeforeNext]);

  return text;
}

export default function Hero() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();
  const prevSlide = () => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const nextSlide = () => setSlide((s) => (s + 1) % HERO_SLIDES.length);
  const typed = useTypewriter(HEADLINE_PHRASES);
  // Reserve width to prevent layout shift as phrases change length.
  const longest = HEADLINE_PHRASES.reduce((a, b) => (b.length > a.length ? b : a), "");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    // Fire-and-forget: capture the lead but don't block the redirect on it.
    joinWaitlist(cleanEmail, "landing_hero").catch(() => {});
    try {
      window.localStorage.setItem("lm_waitlist_email", cleanEmail);
    } catch (_) {}
    navigate(`/signup?email=${encodeURIComponent(cleanEmail)}`);
    setSubmitting(false);
  };

  return (
    <section className="relative overflow-hidden">
      {/* Hero background - sunset sky */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-bg.jpg')" }}
        aria-hidden="true"
      />
      {/* Subtle dark overlay for text legibility */}
      <div
        className="absolute inset-0 pointer-events-none bg-black/40"
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 text-center">
        <h1 className="text-3xl sm:text-5xl lg:text-[60px] font-display font-semibold leading-[1.05] tracking-tight text-white max-w-4xl mx-auto drop-shadow-[0_2px_18px_rgba(0,0,0,0.25)]">
          Type a query. <br className="hidden sm:block" />
          <span className="text-white inline-flex items-baseline justify-center">
            <span className="relative">
              <span aria-hidden className="invisible whitespace-pre">
                {longest}
              </span>
              <span
                className="absolute inset-0 whitespace-pre"
                aria-live="polite"
                data-testid="hero-typewriter"
              >
                {typed}
                <span className="lm-caret bg-white ml-0.5" />
              </span>
            </span>
          </span>
        </h1>

        <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto drop-shadow-[0_1px_10px_rgba(0,0,0,0.25)]">
          Uncover{" "}
          <span className="underline decoration-white/40 decoration-2 underline-offset-4">intent signals</span>
          {" "}and win your next customer across Reddit, LinkedIn, forums and more - built for{" "}
          <span className="underline decoration-white/40 decoration-2 underline-offset-4">busy Indian founders</span>.
        </p>

        <form onSubmit={onSubmit} className="mt-10 max-w-3xl mx-auto" data-testid="hero-waitlist-form">
          <div className="lm-cta-orbit relative rounded-lg p-[2px]">
            <div className="relative flex flex-col sm:flex-row items-stretch gap-2 bg-[color:var(--lm-dark)] rounded-md p-2">
              <div className="relative flex-1 flex items-center">
                <span className="pointer-events-none absolute left-4 text-white flex items-center">
                  <Mail className="w-5 h-5" strokeWidth={2.2} />
                </span>
                <input
                  data-testid="hero-waitlist-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-transparent pl-12 pr-4 py-3.5 text-base text-white placeholder:text-white/45 caret-lm-brand-green outline-none rounded-md"
                  required
                />
              </div>
              <button
                data-testid="hero-waitlist-submit"
                type="submit"
                disabled={submitting}
                className="lm-shine-parent group relative inline-flex items-center justify-center gap-2 bg-lm-secondary hover:bg-lm-secondaryHover disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-md text-base font-semibold transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_10px_24px_-10px_rgba(217,90,64,0.7)] hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <span className="relative z-10">{submitting ? "Saving…" : "Get started"}</span>
                {!submitting && (
                  <ArrowRight
                    className="relative z-10 w-4 h-4 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2.6}
                  />
                )}
              </button>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
            <span className="inline-flex items-center gap-2 text-sm text-white/85">
              <CheckCircle2 className="w-4 h-4 text-lm-brand-green shrink-0" strokeWidth={2} />
              Your first scan is free
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-white/85">
              <CheckCircle2 className="w-4 h-4 text-lm-brand-green shrink-0" strokeWidth={2} />
              10-minute setup
            </span>
          </div>
          {/* Trust-signal chip — only shown once we've filtered ≥1,000 junk posts platform-wide */}
          <div className="mt-4 flex justify-center">
            <JunkFilteredChip minCount={1000} variant="landing" />
          </div>
        </form>

        {/* Real product preview — slider of real screenshots */}
        <div className="relative mt-16 md:mt-20 lm-preview-scroll-scene">
          <div className="lm-preview-zoom relative mx-auto max-w-5xl rounded-lg border border-lm-border bg-lm-surface p-2 md:p-3">
            <div className="flex items-center justify-between px-3 py-2 border-b border-lm-border">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F26F55]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#F1C24B]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#4CB57A]" />
              </div>
              <div className="text-xs font-mono text-lm-text3 truncate">{HERO_SLIDES[slide].url}</div>
              <div className="text-xs font-mono px-2 py-0.5 rounded bg-lm-tint2 text-lm-primary">Live</div>
            </div>
            <div className="relative overflow-hidden rounded-b-md">
              {HERO_SLIDES.map((s, i) => (
                <img
                  key={s.src}
                  src={s.src}
                  alt={s.alt}
                  className={`block w-full h-auto select-none transition-opacity duration-500 ${i === slide ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}
                  draggable="false"
                  loading={i === 0 ? "eager" : "lazy"}
                  width="1600"
                  height="1000"
                />
              ))}
            </div>
          </div>

          {/* Nav arrows */}
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Previous screenshot"
            className="absolute left-1 md:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-lm-surface border border-lm-border shadow-[0_8px_24px_-8px_rgba(0,0,0,0.25)] flex items-center justify-center text-lm-text2 hover:text-lm-primary hover:border-lm-primary/40 hover:scale-105 transition-all z-10"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Next screenshot"
            className="absolute right-1 md:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-lm-surface border border-lm-border shadow-[0_8px_24px_-8px_rgba(0,0,0,0.25)] flex items-center justify-center text-lm-text2 hover:text-lm-primary hover:border-lm-primary/40 hover:scale-105 transition-all z-10"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2.2} />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-5">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Go to screenshot ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-lm-primary" : "w-2 bg-lm-border hover:bg-lm-text3"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
